// Route: /calendar — главная страница календаря «Картотека».
// Тактильная панель управления: сегмент-контрол вида + навигатор периода +
// CTA. Виды month/week/day. Mobile: day-events через Sheet.
//
// ⚠️ НЕ УДАЛЯТЬ. ЭТО НЕ МЁРТВЫЙ КОД, А ОСОЗНАННО ДВА РАЗНЫХ КАЛЕНДАРЯ.
//
// В приложении их два, и они делают разное:
//   - ЭТОТ (`/calendar`) — личный календарь: произвольные события пользователя
//     («Добавить событие»), расписание занятий, экзамены, повторы;
//   - `/courses/calendar` — учебный календарь: уроки, экзамены и повторы,
//     распределённые ПЛАНАМИ курсов. Именно на него ведёт пункт «Календарь»
//     в боковой навигации.
//
// Решение владельца (2026-08-14): личный календарь пока не подключён к
// навигации, применение для него ещё не выбрано — но его НЕ СНОСИТЬ.
// Оба роута объявлены в `modules/calendar.manifest.ts` намеренно.
//
// Ссылка на него всё-таки одна, и она рабочая:
// `components/settings/sync-issues-center.tsx` ведёт сюда при конфликте
// синхронизации записи `calendar_events` — то есть это ещё и посадочная
// страница разбора таких конфликтов. Снос сломал бы этот путь.
//
// URL state:
//   ?view=month|week|day   — текущий view (default month)
//   ?date=YYYY-MM-DD       — anchor date (default — сегодня)
//   ?demo=loading|empty|error|busy
//
// MSW endpoints в apps/web/src/mocks/handlers/calendar.ts.
// Спеки: docs/ui-spec/calendar.{month,week,event.dialog}.{desktop,mobile}.md.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button } from "@studyhub/ui/components/button";
import { Skeleton } from "@studyhub/ui/components/skeleton";
import { CategoryTile } from "@studyhub/ui/components/studyhub/category-tile";
import { SectionHeader } from "@studyhub/ui/components/studyhub/section-header";
import { IconTooltip } from "@studyhub/ui/components/tooltip";
import {
	IconAlertTriangle,
	IconCalendarPlus,
	IconCalendarStar,
	IconChevronLeft,
	IconChevronRight,
	IconMoodEmpty,
} from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { type CalendarEventDto, useCalendarEvents } from "@/components/calendar/_api";
import {
	addDays,
	addMonths,
	addWeeks,
	parseDateKey,
	startOfMonth,
	dateKey as toDateKey,
} from "@/components/calendar/_format";
import { DayEventsSheet } from "@/components/calendar/day-events-sheet";
import { DayView } from "@/components/calendar/day-view";
import { EventDialog, type EventDialogState } from "@/components/calendar/event-dialog";
import { MonthView } from "@/components/calendar/month-view";
import { type CalendarView, ViewSwitcher } from "@/components/calendar/view-switcher";
import { WeekView } from "@/components/calendar/week-view";
import { LIST_PAGE_SHELL } from "@/components/shared/entity-list";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import { useIsLgUp } from "@/lib/platform-detect";

const searchSchema = z.object({
	view: z.enum(["month", "week", "day"]).optional(),
	date: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/)
		.optional(),
	demo: z.enum(["default", "loading", "empty", "error", "busy"]).optional(),
});

export const Route = createFileRoute("/calendar")({
	validateSearch: tolerantSearch(searchSchema),
	component: CalendarPage,
	pendingComponent: PagePendingFallback,
});

function CalendarPage(): React.ReactElement {
	const { t, i18n } = useTranslation("calendar");
	const search = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });
	const isDesktop = useIsLgUp();
	const locale = i18n.language?.startsWith("en") ? "en-US" : "ru-RU";

	const view: CalendarView = search.view ?? "month";
	const anchor = useMemo(() => {
		if (search.date) {
			const parsed = parseDateKey(search.date);
			if (parsed) return parsed;
		}
		return new Date();
	}, [search.date]);

	const eventsQuery = useCalendarEvents(search.demo);
	const events: CalendarEventDto[] = useMemo(
		() => eventsQuery.data?.items ?? [],
		[eventsQuery.data],
	);

	// Sheet/Dialog state.
	const [sheetDate, setSheetDate] = useState<Date | null>(null);
	const [dialogState, setDialogState] = useState<EventDialogState | null>(null);

	const setView = (next: CalendarView) =>
		void navigate({
			search: (prev) => ({ ...prev, view: next }),
			replace: true,
		});
	const setDate = (next: Date) =>
		void navigate({
			search: (prev) => ({ ...prev, date: toDateKey(next) }),
			replace: true,
		});

	const goPrev = () => {
		if (view === "month") setDate(addMonths(startOfMonth(anchor), -1));
		else if (view === "week") setDate(addWeeks(anchor, -1));
		else setDate(addDays(anchor, -1));
	};
	const goNext = () => {
		if (view === "month") setDate(addMonths(startOfMonth(anchor), 1));
		else if (view === "week") setDate(addWeeks(anchor, 1));
		else setDate(addDays(anchor, 1));
	};
	const goToday = () => setDate(new Date());

	const headerLabel = useMemo(() => {
		if (view === "month") {
			return new Intl.DateTimeFormat(locale, {
				month: "long",
				year: "numeric",
			}).format(anchor);
		}
		if (view === "week") {
			const start = new Date(anchor);
			const dow = start.getDay();
			const diff = dow === 0 ? -6 : 1 - dow;
			start.setDate(start.getDate() + diff);
			const end = new Date(start);
			end.setDate(end.getDate() + 6);
			const fmt = new Intl.DateTimeFormat(locale, {
				day: "numeric",
				month: "short",
			});
			return t("weekRange", { from: fmt.format(start), to: fmt.format(end) });
		}
		return new Intl.DateTimeFormat(locale, {
			day: "numeric",
			month: "long",
			year: "numeric",
		}).format(anchor);
	}, [view, anchor, locale, t]);

	function openCreateDialog(initial?: Partial<CalendarEventDto>) {
		setDialogState({ mode: "create", initial: initial ?? null });
	}

	function openEditDialog(event: CalendarEventDto) {
		setDialogState({ mode: "edit", initial: event });
	}

	function handleSelectDay(date: Date) {
		if (isDesktop && view === "month") {
			// На desktop: клик по дню → переключаем в day-view (быстрая навигация).
			setDate(date);
			setView("day");
			return;
		}
		// Mobile / fallback — открываем bottom-sheet с событиями.
		setSheetDate(date);
	}

	return (
		<div className={LIST_PAGE_SHELL}>
			<SectionHeader
				category="time"
				icon={<IconCalendarStar stroke={1.75} />}
				title={t("title")}
				description={t("subtitle")}
				actions={
					<Button
						type="button"
						className="cursor-pointer"
						onClick={() => openCreateDialog({ startsAt: new Date().toISOString() })}
					>
						<IconCalendarPlus className="size-4" />
						{t("events.addEvent")}
					</Button>
				}
			/>

			{/* Тактильная панель управления — сегмент вида + навигатор периода */}
			<div className="flex flex-wrap items-center gap-3 rounded-[var(--radius-card)] border border-border bg-card p-2 shadow-e1">
				<ViewSwitcher value={view} onChange={setView} />

				<div className="flex items-center gap-1">
					<IconTooltip label={t("nav.prev")}>
						<Button
							type="button"
							variant="outline"
							size="icon"
							className="cursor-pointer"
							onClick={goPrev}
							aria-label={t("nav.prev")}
						>
							<IconChevronLeft className="size-4" />
						</Button>
					</IconTooltip>
					<Button
						type="button"
						variant="outline"
						size="default"
						className="cursor-pointer pointer-coarse:h-11"
						onClick={goToday}
					>
						{t("nav.today")}
					</Button>
					<IconTooltip label={t("nav.next")}>
						<Button
							type="button"
							variant="outline"
							size="icon"
							className="cursor-pointer"
							onClick={goNext}
							aria-label={t("nav.next")}
						>
							<IconChevronRight className="size-4" />
						</Button>
					</IconTooltip>
				</div>

				<div className="num ml-1 min-w-0 flex-1 truncate text-sm font-semibold text-foreground capitalize tabular-nums">
					{headerLabel}
				</div>
			</div>

			{eventsQuery.isError ? (
				<CalendarStateCard
					tone="error"
					icon={<IconAlertTriangle stroke={1.75} />}
					title={t("errors.loadFailed")}
					action={
						<Button
							type="button"
							variant="outline"
							className="cursor-pointer"
							onClick={() => void eventsQuery.refetch()}
						>
							{t("errors.retry")}
						</Button>
					}
				/>
			) : eventsQuery.isLoading ? (
				<CalendarSkeleton view={view} />
			) : events.length === 0 && view === "month" ? (
				<CalendarStateCard
					tone="empty"
					icon={<IconMoodEmpty stroke={1.75} />}
					title={t("month.empty")}
					action={
						<Button type="button" className="cursor-pointer" onClick={() => openCreateDialog()}>
							<IconCalendarPlus className="size-4" />
							{t("month.addOne")}
						</Button>
					}
				/>
			) : view === "month" ? (
				<MonthView
					anchor={anchor}
					events={events}
					onSelectDay={handleSelectDay}
					onSelectEvent={openEditDialog}
				/>
			) : view === "week" ? (
				<WeekView
					anchor={anchor}
					events={events}
					onSelectEvent={openEditDialog}
					onCreateAtSlot={(startsAt, endsAt) =>
						openCreateDialog({
							startsAt: startsAt.toISOString(),
							endsAt: endsAt.toISOString(),
							type: "lesson",
						})
					}
				/>
			) : (
				<DayView
					anchor={anchor}
					events={events}
					onSelectEvent={openEditDialog}
					onCreateAtSlot={(startsAt, endsAt) =>
						openCreateDialog({
							startsAt: startsAt.toISOString(),
							endsAt: endsAt.toISOString(),
							type: "lesson",
						})
					}
				/>
			)}

			<DayEventsSheet
				open={sheetDate !== null}
				date={sheetDate}
				events={events}
				onOpenChange={(o) => {
					if (!o) setSheetDate(null);
				}}
				onSelectEvent={(ev) => {
					setSheetDate(null);
					openEditDialog(ev);
				}}
				onCreate={(date) => {
					setSheetDate(null);
					const start = new Date(date);
					start.setHours(new Date().getHours(), 0, 0, 0);
					const end = new Date(start);
					end.setHours(end.getHours() + 1);
					openCreateDialog({
						startsAt: start.toISOString(),
						endsAt: end.toISOString(),
					});
				}}
			/>

			<EventDialog
				open={dialogState !== null}
				state={dialogState}
				onOpenChange={(o) => {
					if (!o) setDialogState(null);
				}}
			/>
		</div>
	);
}

/** Спокойное «старт» / «ошибка» состояние в приподнятой панели. */
function CalendarStateCard({
	tone,
	icon,
	title,
	action,
}: {
	tone: "empty" | "error";
	icon: React.ReactElement;
	title: string;
	action: React.ReactNode;
}) {
	return (
		<div className="flex flex-col items-center gap-4 rounded-[var(--radius-panel)] border border-border bg-card px-6 py-16 text-center shadow-e2">
			<CategoryTile category={tone === "error" ? "streak" : "time"} icon={icon} size="xl" />
			<p className="text-sm font-medium text-foreground">{title}</p>
			{action}
		</div>
	);
}

function CalendarSkeleton({ view }: { view: CalendarView }) {
	if (view === "month") {
		return (
			<div className="overflow-hidden rounded-[var(--radius-panel)] border border-border bg-card shadow-e2">
				<div className="grid grid-cols-7 border-b border-border bg-[var(--sh-well)]">
					{Array.from({ length: 7 }).map((_, i) => (
						<div key={i} className="py-2.5">
							<Skeleton className="mx-auto h-3 w-7" />
						</div>
					))}
				</div>
				<div className="grid grid-cols-7 grid-rows-6">
					{Array.from({ length: 42 }).map((_, i) => (
						<div
							key={i}
							className="flex min-h-[7rem] flex-col gap-1.5 border-r border-b border-border p-1.5 sm:min-h-[8.75rem]"
						>
							<Skeleton className="size-6 rounded-[7px]" />
							<Skeleton className="h-5 w-full rounded-[7px]" />
							<Skeleton className="h-5 w-3/4 rounded-[7px]" />
						</div>
					))}
				</div>
			</div>
		);
	}
	return (
		<div className="overflow-hidden rounded-[var(--radius-panel)] border border-border bg-card shadow-e2">
			<Skeleton className="h-14 w-full rounded-none" />
			<Skeleton className="h-[36rem] w-full rounded-none" />
		</div>
	);
}
